import {
  NexorAPIError,
  NexorAuthError,
  NexorNetworkError,
  NexorValidationError,
} from "./errors";
import type { ClientOptions, RequestOptions } from "./types";

const DEFAULT_BASE_URL = "https://api.getnexor.ai";
const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_MAX_RETRIES = 2;
const SDK_VERSION = "0.1.0";

export interface ResolvedConfig {
  apiKey: string;
  baseUrl: string;
  timeoutMs: number;
  maxRetries: number;
  fetchImpl: typeof fetch;
  userAgent: string;
}

export function resolveConfig(opts: ClientOptions): ResolvedConfig {
  if (!opts || typeof opts.apiKey !== "string" || opts.apiKey.trim() === "") {
    throw new Error(
      "Nexor SDK: `apiKey` is required. Get one in the dashboard at https://app.getnexor.ai",
    );
  }
  const fetchImpl =
    opts.fetch ??
    (typeof globalThis !== "undefined" && typeof globalThis.fetch === "function"
      ? globalThis.fetch.bind(globalThis)
      : (undefined as unknown as typeof fetch));
  if (!fetchImpl) {
    throw new Error(
      "Nexor SDK: no global fetch found. Pass a `fetch` implementation via init() or upgrade to Node 18+.",
    );
  }
  const ua = `nexor-sdk-js/${SDK_VERSION}${opts.userAgentSuffix ? ` ${opts.userAgentSuffix}` : ""}`;
  return {
    apiKey: opts.apiKey.trim(),
    baseUrl: stripTrailingSlash(opts.baseUrl ?? DEFAULT_BASE_URL),
    timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    maxRetries: opts.maxRetries ?? DEFAULT_MAX_RETRIES,
    fetchImpl,
    userAgent: ua,
  };
}

interface HttpRequest {
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  path: string;
  query?: Record<string, string | number | boolean | string[] | undefined | null>;
  body?: unknown;
  options?: RequestOptions;
}

export async function request<T>(
  cfg: ResolvedConfig,
  req: HttpRequest,
): Promise<T> {
  const url = buildUrl(cfg.baseUrl, req.path, req.query);
  const baseHeaders: Record<string, string> = {
    "X-API-Key": cfg.apiKey,
    Accept: "application/json",
    "User-Agent": cfg.userAgent,
  };
  if (req.options?.idempotencyKey) {
    baseHeaders["Idempotency-Key"] = req.options.idempotencyKey;
  }
  let body: BodyInit | undefined;
  if (req.body !== undefined) {
    baseHeaders["Content-Type"] = "application/json";
    body = JSON.stringify(req.body);
  }

  const timeoutMs = req.options?.timeoutMs ?? cfg.timeoutMs;
  const maxRetries = req.options?.maxRetries ?? cfg.maxRetries;
  let lastErr: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const headers = baseHeaders;
    const controller = new AbortController();
    const externalSignal = req.options?.signal;
    const onAbort = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener("abort", onAbort);
    }
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await cfg.fetchImpl(url, {
        method: req.method,
        headers,
        body,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener("abort", onAbort);

      const requestId = res.headers.get("x-request-id") ?? undefined;

      if (res.ok) {
        if (res.status === 204) return undefined as T;
        const text = await res.text();
        if (!text) return undefined as T;
        try {
          return JSON.parse(text) as T;
        } catch {
          throw new NexorAPIError({
            status: res.status,
            message: "Failed to parse JSON response",
            body: text,
            requestId,
          });
        }
      }

      // Non-2xx: parse and throw a typed error.
      const errBody = await safeJson(res);
      const message = pickMessage(errBody) ?? `HTTP ${res.status}`;
      const err = makeError(res.status, message, errBody, requestId);

      // Retry on 429 + 5xx with exponential backoff.
      if (shouldRetry(res.status) && attempt < maxRetries) {
        await sleep(backoffMs(attempt, res));
        lastErr = err;
        continue;
      }
      throw err;
    } catch (err) {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener("abort", onAbort);
      if (err instanceof NexorAPIError) throw err;
      const isAbort =
        err instanceof Error &&
        (err.name === "AbortError" || /abort/i.test(err.message));
      if (isAbort && externalSignal?.aborted) throw err;
      // Network or timeout: retry.
      lastErr = err;
      if (attempt < maxRetries) {
        await sleep(backoffMs(attempt));
        continue;
      }
      throw new NexorNetworkError(
        isAbort ? `Request timed out after ${timeoutMs}ms` : `Network error: ${(err as Error)?.message ?? String(err)}`,
        err,
      );
    }
  }
  throw lastErr ?? new NexorNetworkError("Request failed");
}

function makeError(
  status: number,
  message: string,
  body: unknown,
  requestId?: string,
): NexorAPIError {
  if (status === 401 || status === 403) {
    return new NexorAuthError({ message, body, requestId });
  }
  if (status === 400 || status === 422) {
    return new NexorValidationError({ message, body, requestId });
  }
  return new NexorAPIError({ status, message, body, requestId });
}

function pickMessage(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const b = body as Record<string, unknown>;
  if (typeof b.message === "string") return b.message;
  if (typeof b.error === "string") return b.error;
  return undefined;
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    const txt = await res.text();
    if (!txt) return null;
    try {
      return JSON.parse(txt);
    } catch {
      return txt;
    }
  } catch {
    return null;
  }
}

function shouldRetry(status: number): boolean {
  return status === 429 || (status >= 500 && status <= 599);
}

function backoffMs(attempt: number, res?: Response): number {
  if (res) {
    const ra = res.headers.get("retry-after");
    if (ra) {
      const n = Number(ra);
      if (!Number.isNaN(n) && n > 0) return Math.min(n * 1000, 10_000);
    }
  }
  const base = 300 * 2 ** attempt;
  return base + Math.floor(Math.random() * 200);
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function stripTrailingSlash(s: string): string {
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: HttpRequest["query"],
): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  const qs = encodeQuery(query);
  return `${baseUrl}${p}${qs ? `?${qs}` : ""}`;
}

function encodeQuery(query?: HttpRequest["query"]): string {
  if (!query) return "";
  const parts: string[] = [];
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v.join(","))}`);
    } else {
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
  }
  return parts.join("&");
}
